/**
 * Outbound webhook endpoints + delivery history.
 *
 * Wraps the SECURITY DEFINER RPCs in `core.rpc_*_webhook_*` (see
 * `supabase/migrations/20260525073000_core_outbound_webhooks_and_api.sql`).
 * Every mutation requires the caller to be an org admin -- enforced
 * inside the RPC, not just on the UI.
 *
 * The plaintext webhook secret is returned ONCE at create / rotate time.
 * Don't persist it on the client; show it in the modal and discard.
 *
 * Implementation note: every RPC call goes through
 * `(supabase() as any).rpc(...)` because the Database typegen doesn't
 * know about these RPCs until `pnpm gen:types` is run again. The cast
 * mirrors `helpMessages.ts` and friends.
 */
import { supabase } from '../client';

export interface WebhookEndpoint {
  id: string;
  url: string;
  description: string | null;
  enabled: boolean;
  event_filter: string[];
  max_attempts: number;
  secret_prefix: string;
  last_success_at: string | null;
  last_failure_at: string | null;
  last_failure_status: number | null;
  created_at: string;
  updated_at: string;
}

export interface WebhookDelivery {
  id: string;
  endpoint_id: string;
  event_type: string;
  attempt: number;
  status: 'pending' | 'succeeded' | 'failed' | 'dropped';
  response_status: number | null;
  response_ms: number | null;
  response_body_excerpt: string | null;
  scheduled_at: string;
  attempted_at: string | null;
  next_retry_at: string | null;
  created_at: string;
}

export interface ListWebhookEndpointsInput {
  orgId: string;
  cursor?: { at: string; id: string } | null;
  limit?: number;
}

export async function listWebhookEndpoints(
  input: ListWebhookEndpointsInput,
): Promise<{ rows: WebhookEndpoint[]; nextCursor: { at: string; id: string } | null }> {
  const limit = Math.max(1, Math.min(input.limit ?? 25, 100));
  // deno-lint-ignore-file no-explicit-any
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_list_webhook_endpoints', {
      p_org_id: input.orgId,
      p_cursor_at: input.cursor?.at ?? null,
      p_cursor_id: input.cursor?.id ?? null,
      p_limit: limit,
    });
  if (error) throw error;
  const rows = ((data ?? []) as unknown) as WebhookEndpoint[];
  const last = rows[rows.length - 1];
  return {
    rows,
    nextCursor:
      rows.length >= limit && last ? { at: last.created_at, id: last.id } : null,
  };
}

export interface CreateWebhookEndpointInput {
  orgId: string;
  url: string;
  description?: string;
  eventFilter?: string[];
  maxAttempts?: number;
}

export async function createWebhookEndpoint(
  input: CreateWebhookEndpointInput,
): Promise<{ id: string; secret_plain: string; secret_prefix: string }> {
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_create_webhook_endpoint', {
      p_org_id: input.orgId,
      p_url: input.url,
      p_description: input.description ?? null,
      p_event_filter: input.eventFilter ?? ['*'],
      p_max_attempts: input.maxAttempts ?? 5,
    });
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as
    | { id: string; secret_plain: string; secret_prefix: string }
    | undefined;
  if (!row) throw new Error('createWebhookEndpoint: no row returned');
  return row;
}

export interface UpdateWebhookEndpointInput {
  id: string;
  url?: string;
  description?: string | null;
  eventFilter?: string[];
  enabled?: boolean;
  maxAttempts?: number;
}

export async function updateWebhookEndpoint(
  input: UpdateWebhookEndpointInput,
): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_update_webhook_endpoint', {
      p_id: input.id,
      p_url: input.url ?? null,
      p_description: input.description ?? null,
      p_event_filter: input.eventFilter ?? null,
      p_enabled: input.enabled ?? null,
      p_max_attempts: input.maxAttempts ?? null,
    });
  if (error) throw error;
}

export async function rotateWebhookEndpointSecret(
  endpointId: string,
): Promise<{ secret_plain: string; secret_prefix: string }> {
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_rotate_webhook_endpoint_secret', { p_id: endpointId });
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as
    | { secret_plain: string; secret_prefix: string }
    | undefined;
  if (!row) throw new Error('rotateWebhookEndpointSecret: no row returned');
  return row;
}

export async function deleteWebhookEndpoint(endpointId: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_delete_webhook_endpoint', { p_id: endpointId });
  if (error) throw error;
}

export interface ListWebhookDeliveriesInput {
  endpointId: string;
  cursor?: { at: string; id: string } | null;
  limit?: number;
}

export async function listWebhookDeliveries(
  input: ListWebhookDeliveriesInput,
): Promise<{ rows: WebhookDelivery[]; nextCursor: { at: string; id: string } | null }> {
  const limit = Math.max(1, Math.min(input.limit ?? 50, 200));
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_list_webhook_deliveries', {
      p_endpoint_id: input.endpointId,
      p_cursor_at: input.cursor?.at ?? null,
      p_cursor_id: input.cursor?.id ?? null,
      p_limit: limit,
    });
  if (error) throw error;
  const rows = ((data ?? []) as unknown) as WebhookDelivery[];
  const last = rows[rows.length - 1];
  return {
    rows,
    nextCursor:
      rows.length >= limit && last ? { at: last.created_at, id: last.id } : null,
  };
}

export async function retryWebhookDelivery(deliveryId: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_retry_webhook_delivery', { p_delivery_id: deliveryId });
  if (error) throw error;
}

/** Enqueues a synthetic `webhook.test` event delivery for the endpoint. */
export async function testWebhookEndpoint(endpointId: string): Promise<string> {
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_test_webhook_endpoint', { p_endpoint_id: endpointId });
  if (error) throw error;
  return data as unknown as string;
}

/**
 * Canonical event-type catalog the UI shows in the multi-select.
 * Sourced from the modules that already call core.emit_event today.
 */
export const KNOWN_WEBHOOK_EVENTS: Array<{ type: string; description: string }> = [
  { type: '*', description: 'All events' },
  { type: 'order.created', description: 'A new operator-side order is created' },
  { type: 'order.shipped', description: 'An order is marked shipped' },
  { type: 'order.delivered', description: 'A carrier reports delivered' },
  { type: 'client.created', description: 'A client is added to the CRM' },
  { type: 'client.updated', description: 'A client is updated' },
  { type: 'vendor.created', description: 'A supplier is added to vendors' },
  { type: 'vendor.updated', description: 'A supplier is updated' },
  { type: 'vendor.claimed', description: 'A shop vendor profile is claimed' },
  { type: 'payment.recorded', description: 'A payment is recorded against an order' },
  { type: 'shipment.tracking_added', description: 'A tracking number is attached' },
  { type: 'webhook.test', description: 'Synthetic test event from settings' },
];
